import { HttpClient } from '@adapters/HttpClient';
import { Cart, CartFilters, CartPage, CartsApiResponse, CreateCartRequest, Product } from '../models/Cart';
import { UpdateCartRequest } from '../services/CartService';

export class CartApiAdapter {
  constructor(private httpClient: HttpClient) {}

  // 🎯 Obtener todos los carritos (sin paginación)
  async getCarts(filters: CartFilters = {}): Promise<Cart[]> {
    const response = await this.getCartsPaginated(filters);
    return response.carts;
  }

  // 🎯 Enriquecer un carrito con información adicional
  private async enrichCart(cart: Cart): Promise<Cart> {
    // Calcular stock total si hay stock_location_carts
    let calculatedStock = 0;
    if (cart.stock_location_carts && cart.stock_location_carts.length > 0) {
      calculatedStock = cart.stock_location_carts.reduce((sum, item) => sum + (item.qty || 0), 0);
    }

    // Obtener preparation_id o merchandise_id del primer stock_location_cart
    const firstStockLocation = cart.stock_location_carts?.[0];
    let preparationCode: string | undefined;
    let preparationId: number | undefined;
    let preparationStatus: number | undefined;
    let merchandiseCode: string | undefined;
    let merchandiseId: number | undefined;

    if (firstStockLocation) {
      try {
        if (firstStockLocation.preparation_id) {
          preparationId = firstStockLocation.preparation_id;
          const preparation = await this.httpClient.get<{ code: string; status: number }>(
            `/preparations/${firstStockLocation.preparation_id}`,
            {},
            {},
            false, // No mostrar loading global
          );
          preparationCode = preparation.code;
          preparationStatus = preparation.status;
        } else if (firstStockLocation.merchandise_id) {
          merchandiseId = firstStockLocation.merchandise_id;
          const merchandiseResponse = await this.httpClient.get<{ data: Array<{ code: string }> }>(
            `/merchandise/${firstStockLocation.merchandise_id}`,
            {},
            {},
            false, // No mostrar loading global
          );
          merchandiseCode = merchandiseResponse.data?.[0]?.code;
        }
      } catch (error) {
        console.warn('Error obteniendo código de preparation/merchandise:', error);
      }
    }

    return {
      ...cart,
      stock: calculatedStock || cart.stock, // Usar el calculado si hay, sino el original
      preparation_code: preparationCode,
      preparation_id: preparationId,
      preparation_status: preparationStatus,
      merchandise_code: merchandiseCode,
      merchandise_id: merchandiseId,
    };
  }

  // 🎯 Nuevo método para scroll infinito - retorna respuesta completa
  async getCartsPaginated(filters: CartFilters = {}): Promise<CartPage> {
    const {
      searchTerm = '',
      productId,
      typeId,
      teamId,
      type_ids,
      product_ids,
      sortBy,
      sortDirection,
      page = 1,
      limit = 10,
      advanced_filters,
    } = filters;

    // 🎯 Construir parámetros de consulta
    const params: Record<string, any> = {
      page,
      per_page: limit,
    };

    // 🎯 Agregar filtros solo si tienen valor
    if (searchTerm) params.search = searchTerm;
    if (productId) params.product_id = productId;
    if (typeId) params.type_id = typeId;
    if (teamId) params.team_id = teamId;
    if (type_ids) params.type_ids = type_ids;
    if (product_ids) params.product_ids = product_ids;
    if (sortBy) params.order_by = sortBy;
    if (sortDirection) params.order_direction = sortDirection;

    // 🎯 Procesar advanced_filters si existen
    if (advanced_filters?.filters) {
      advanced_filters.filters.forEach((filter: any, index: number) => {
        params[`advanced_filters[filters][${index}][column]`] = filter.column;
        params[`advanced_filters[filters][${index}][operator]`] = filter.operator;

        // 🎯 Manejar valores de array para operadores 'in' y 'not_in'
        if (filter.operator === 'in' || filter.operator === 'not_in') {
          if (Array.isArray(filter.value)) {
            filter.value.forEach((val: any, valIndex: number) => {
              params[`advanced_filters[filters][${index}][value][${valIndex}]`] = val;
            });
          } else {
            params[`advanced_filters[filters][${index}][value][0]`] = filter.value;
          }
        } else {
          params[`advanced_filters[filters][${index}][value]`] = filter.value;
        }
      });
      params['advanced_filters[logic]'] = advanced_filters.logic || 'AND';
    }

    // 🎯 Configurar loading global solo para la primera página
    const showGlobalLoading = page === 1;

    const response = await this.httpClient.get<CartsApiResponse>('/carts', params, {}, showGlobalLoading);

    // 🎯 Enriquecer cada carrito con información adicional
    const enrichedCarts = await Promise.all(response.data.map((cart) => this.enrichCart(cart)));

    return {
      carts: enrichedCarts,
      nextPage: response.meta.currentPage < response.meta.lastPage ? response.meta.currentPage + 1 : undefined,
      hasMore: response.meta.currentPage < response.meta.lastPage,
      total: response.meta.total,
    };
  }

  // 🎯 Crear nuevo carrito
  async createCart(data: CreateCartRequest): Promise<Cart> {
    const response = await this.httpClient.post<Cart>('/carts', data);
    return response;
  }

  // 🎯 Actualizar carrito existente
  async updateCart(id: number, data: UpdateCartRequest): Promise<Cart> {
    const response = await this.httpClient.put<Cart>(`/carts/${id}`, data);
    return response;
  }

  // 🎯 Eliminar carrito
  async deleteCart(id: number): Promise<void> {
    await this.httpClient.delete(`/carts/${id}`);
  }

  // 🎯 Obtener productos para selectores
  async getProducts(): Promise<Product[]> {
    const response = await this.httpClient.get<Product[]>('/products', { fields: 'basic' });
    return response;
  }
}
